// src/components/AttachmentComponent.js
import React, { useState, useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  Alert,
  Image,
  Linking,
  Platform,
  Modal,
  TextInput,
} from 'react-native';
import { Button, Card, Text, IconButton, Chip, Badge } from 'react-native-paper';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import { AVAQualisTheme } from '../theme/theme';

// Importar estilos com fallback
let styles;
try {
  styles = require('./AttachmentComponent.css').styles;
  // Garantir que temos objetos necessários
  if (!styles.placeholderText) {
    styles.placeholderText = { color: '#999' };
  }
} catch (error) {
  console.warn('⚠️ Arquivo de estilos não encontrado, usando estilos padrão');
  // Estilos padrão mínimos
  styles = {
    container: {},
    actionButtons: {},
    attachmentsList: {},
    attachmentsTitle: {},
    attachmentCard: {},
    attachmentCardHovered: {},
    attachmentContent: {},
    attachmentHeader: {},
    attachmentInfo: {},
    attachmentIcon: {},
    thumbnailImage: { width: 50, height: 50, borderRadius: 4 },
    videoThumbnail: {},
    videoIcon: {},
    linkIcon: {},
    linkIconText: {},
    fileIcon: {},
    supabaseBadge: {},
    supabaseBadgeText: {},
    localOnlyBadge: {},
    localOnlyBadgeText: {},
    mongoBadge: {},
    mongoBadgeText: {},
    hoverOverlay: {},
    hoverIcon: {},
    hoverText: {},
    attachmentDetails: {},
    attachmentName: {},
    attachmentSize: {},
    attachmentDescription: {},
    attachmentValidity: {},
    attachmentValidityExpired: {},
    attachmentValidityWarning: {},
    syncStatus: {},
    attachmentActions: {},
    viewButtonMain: {},
    viewButtonMainHovered: {},
    secondaryActionButton: {},
    previewTooltip: {},
    tooltipText: {},
    tooltipSubtext: {},
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: 8,
      padding: 20,
      width: '90%',
      maxWidth: 500,
    },
    modalTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      marginBottom: 15,
    },
    modalLabel: {
      fontSize: 14,
      fontWeight: 'bold',
      marginBottom: 5,
    },
    modalInput: {
      borderWidth: 1,
      borderColor: '#ddd',
      borderRadius: 4,
      padding: 10,
      marginBottom: 10,
    },
    modalInputMultiline: {
      minHeight: 80,
      textAlignVertical: 'top',
    },
    modalCharCount: {
      fontSize: 12,
      marginBottom: 10,
    },
    validitySection: {},
    validityToggle: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 10,
    },
    validityCheckbox: {
      width: 20,
      height: 20,
      borderWidth: 2,
      borderColor: '#ddd',
      borderRadius: 4,
      marginRight: 10,
    },
    validityCheckboxChecked: {
      backgroundColor: AVAQualisTheme.colors.primary,
      borderColor: AVAQualisTheme.colors.primary,
    },
    validityCheckmark: {
      color: 'white',
      textAlign: 'center',
    },
    validityToggleText: {},
    dateInputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    dateInput: {
      flex: 1,
      borderWidth: 1,
      borderColor: '#ddd',
      borderRadius: 4,
      padding: 10,
      marginRight: 10,
    },
    calendarButton: {
      padding: 10,
    },
    calendarButtonText: {
      fontSize: 20,
    },
    modalButtons: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 20,
    },
    modalButtonCancel: {
      flex: 1,
      marginRight: 10,
      padding: 12,
      borderRadius: 4,
      backgroundColor: '#f0f0f0',
      alignItems: 'center',
    },
    modalButtonSave: {
      flex: 1,
      padding: 12,
      borderRadius: 4,
      backgroundColor: AVAQualisTheme.colors.primary,
      alignItems: 'center',
    },
    modalButtonDisabled: {
      backgroundColor: '#ccc',
    },
    modalButtonText: {
      fontSize: 16,
      fontWeight: 'bold',
    },
    modalButtonTextCancel: {
      color: '#666',
    },
    modalButtonTextSave: {
      color: 'white',
    },
    confirmationDialog: {},
    confirmationIcon: {
      fontSize: 48,
      textAlign: 'center',
      marginBottom: 10,
    },
    confirmationMessage: {
      textAlign: 'center',
      marginBottom: 20,
    },
    confirmationButtons: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    confirmationButtonCancel: {
      flex: 1,
      marginRight: 10,
      padding: 12,
      borderRadius: 4,
      backgroundColor: '#f0f0f0',
      alignItems: 'center',
    },
    confirmationButtonDelete: {
      flex: 1,
      padding: 12,
      borderRadius: 4,
      backgroundColor: AVAQualisTheme.colors.error,
      alignItems: 'center',
    },
    noteContainer: {
      backgroundColor: '#f0f8ff',
      padding: 10,
      borderRadius: 4,
      marginTop: 10,
    },
    noteText: {
      fontSize: 12,
      color: '#666',
    },
    smallModalContent: {
      maxWidth: 400,
    },
    placeholderText: {
      color: '#999',
    },
    findingsInput: {
      borderWidth: 1,
      borderColor: AVAQualisTheme.colors.outline,
      borderRadius: 4,
      padding: 8,
      minHeight: 100,
      textAlignVertical: 'top',
    },
  };
}

const AttachmentComponent = ({
  questionId,
  attachments = [],
  onAttachmentsChange,
  disabled = false,
  onViewAttachment,
  questionCode = '',
  localFileData: externalLocalFileData,
  onLocalDataChange,
}) => {
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkDescription, setLinkDescription] = useState('');
  const [linkHasValidity, setLinkHasValidity] = useState(false);
  const [linkValidityDate, setLinkValidityDate] = useState('');

  const [descriptionDialog, setDescriptionDialog] = useState({
    visible: false,
    type: '',
    file: null,
    description: '',
    hasValidity: false,
    validityDate: '',
  });

  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState({
    visible: false,
    attachment: null,
  });
  const [imagePreviewModal, setImagePreviewModal] = useState({
    visible: false,
    imageUrl: '',
    imageName: '',
  });
  const [editValidityDialog, setEditValidityDialog] = useState({
    visible: false,
    attachment: null,
    newDate: '',
  });

  const [hoveredAttachment, setHoveredAttachment] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Estado para controlar as permissões da câmera
  const [hasCameraPermission, setHasCameraPermission] = useState(null);
  const [hasMediaLibraryPermission, setHasMediaLibraryPermission] = useState(null);

  // IMPORTANTE: Estado para armazenar dados dos arquivos locais
  const [localFileData, setLocalFileData] = useState(externalLocalFileData || {});

  // Limites máximos
  const MAX_PHOTOS = 10;
  const MAX_FILES = 10;
  const MAX_LINKS = 10;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  // Contar anexos por tipo
  const photoCount = attachments.filter(a => a.type === 'photo').length;
  const fileCount = attachments.filter(a => a.type === 'file').length;
  const linkCount = attachments.filter(a => a.type === 'link').length;

  // Efeito para verificar permissões ao carregar o componente
  useEffect(() => {
    (async () => {
      // Verificar permissões iniciais
      try {
        if (Platform.OS !== 'web') {
          const cameraStatus = await ImagePicker.requestCameraPermissionsAsync();
          setHasCameraPermission(cameraStatus.status === 'granted');

          const mediaLibraryStatus = await ImagePicker.requestMediaLibraryPermissionsAsync();
          setHasMediaLibraryPermission(mediaLibraryStatus.status === 'granted');

          console.log('📱 Status das permissões:', {
            camera: cameraStatus.status,
            mediaLibrary: mediaLibraryStatus.status,
          });
        }
      } catch (error) {
        console.log('❌ Erro ao verificar permissões:', error);
        // Definir como null em caso de erro, para que possamos tentar novamente quando necessário
        setHasCameraPermission(null);
        setHasMediaLibraryPermission(null);
      }
    })();
  }, []);

  // Função para gerar ID único
  const generateId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `attachment-${timestamp}-${random}`;
  };

  // Função para obter emoji do tipo de arquivo
  const getFileEmoji = (mimeType, fileName) => {
    if (!mimeType && !fileName) return '📎';

    const type = mimeType?.toLowerCase() || fileName?.toLowerCase() || '';

    if (type.includes('pdf')) return '📄';
    if (type.includes('doc') || type.includes('word')) return '📝';
    if (type.includes('sheet') || type.includes('excel') || type.includes('csv')) return '📊';
    if (type.includes('zip') || type.includes('rar') || type.includes('7z')) return '🗜️';
    if (
      type.includes('image') ||
      type.includes('jpg') ||
      type.includes('png') ||
      type.includes('gif')
    )
      return '🖼️';
    if (type.includes('video') || type.includes('mp4') || type.includes('avi')) return '🎥';
    if (type.includes('audio') || type.includes('mp3') || type.includes('wav')) return '🎵';
    if (type.includes('text') || type.includes('txt')) return '📃';
    if (type.includes('presentation') || type.includes('ppt')) return '📽️';

    return '📎';
  };

  // Gerar nome do arquivo organizado
  const generateDownloadFileName = attachment => {
    const formattedQuestionCode = questionCode ? questionCode.replace(/\./g, '_') : 'Requisito';
    const originalName = attachment.name || `${attachment.type}_${Date.now()}`;

    let extension = '';
    if (originalName.includes('.')) {
      extension = originalName.substring(originalName.lastIndexOf('.'));
    } else if (attachment.mimeType) {
      const mimeToExt = {
        'image/jpeg': '.jpg',
        'image/jpg': '.jpg',
        'image/png': '.png',
        'image/gif': '.gif',
        'image/webp': '.webp',
        'application/pdf': '.pdf',
        'application/zip': '.zip',
        'application/x-zip-compressed': '.zip',
        'text/plain': '.txt',
        'application/msword': '.doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
        'video/mp4': '.mp4',
        'video/avi': '.avi',
        'video/mov': '.mov',
      };
      extension = mimeToExt[attachment.mimeType.toLowerCase()] || '.bin';
    } else {
      extension = '.bin';
    }

    const baseName = originalName.includes('.')
      ? originalName.substring(0, originalName.lastIndexOf('.'))
      : originalName;

    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const timestamp = `${hours}h${minutes}m`;

    return `${formattedQuestionCode}_${baseName}_${timestamp}${extension}`;
  };

  // Função para converter File/Blob para base64
  const fileToBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // CORREÇÃO PRINCIPAL: Visualizar anexos com melhor suporte para arquivos locais
  const handleViewAttachment = attachment => {
  console.log('🔍 Tentando visualizar anexo:', {
    id: attachment.id,
    hasBase64: !!attachment.base64,
    hasUrl: !!attachment.url,
    type: attachment.type,
  });

  // Se for uma foto
  if (attachment.type === 'photo') {
    // Priorizar base64 salvo
    let imageUrl = attachment.base64 || attachment.url || attachment.uri;

    if (imageUrl) {
      console.log('📸 Abrindo preview de imagem');
      setImagePreviewModal({
        visible: true,
        imageUrl: imageUrl,
        imageName: attachment.name || 'Imagem',
      });
      return;
    } else {
      Alert.alert(
        '⚠️ Imagem não disponível',
        'A imagem não foi salva corretamente. Tente anexar novamente.',
        [{ text: 'OK' }]
      );
      return;
    }
  }

  // Se for um link
  if (attachment.type === 'link') {
    openLink(attachment.url);
    return;
  }

  // Para outros tipos de arquivo
  if (Platform.OS === 'web') {
    // Na web, tentar abrir arquivo
    if (attachment.base64) {
      // Se tem base64, abrir diretamente
      const link = document.createElement('a');
      link.href = attachment.base64;
      link.download = attachment.name || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (attachment.url && attachment.url.startsWith('blob:')) {
      // Se tem URL blob válida
      window.open(attachment.url, '_blank');
    } else {
      // Mostrar informações do arquivo
      showFileInfo(attachment);
    }
  } else {
    // No mobile
    if (attachment.base64 && attachment.type === 'file') {
      // Para arquivos com base64, mostrar opção de compartilhar
      Alert.alert(
        `📎 ${attachment.name || 'Arquivo'}`,
        `Tipo: ${attachment.mimeType || 'Não especificado'}\n` +
        `Tamanho: ${attachment.size ? formatFileSize(attachment.size) : 'Desconhecido'}\n` +
        `Descrição: ${attachment.description || 'Sem descrição'}`,
        [
          { text: 'OK' },
          {
            text: '📤 Compartilhar',
            onPress: () => {
              // Implementar compartilhamento se necessário
              Alert.alert('ℹ️', 'Arquivo salvo localmente. Será enviado ao servidor quando fizer upload.');
            }
          }
        ]
      );
    } else {
      showFileInfo(attachment);
    }
  }
};

  // Função auxiliar para mostrar informações do arquivo
  const showFileInfo = attachment => {
    Alert.alert(
      `📎 ${attachment.name || 'Arquivo'}`,
      `Tipo: ${attachment.mimeType || 'Não especificado'}\n` +
        `Tamanho: ${attachment.size ? formatFileSize(attachment.size) : 'Desconhecido'}\n` +
        `Descrição: ${attachment.description || 'Sem descrição'}\n\n` +
        `Este arquivo está armazenado localmente e será enviado ao servidor quando você salvar o formulário.`,
      [
        { text: 'OK' },
        attachment.type === 'file' &&
          Platform.OS === 'web' && {
            text: '💾 Baixar',
            onPress: () => downloadLocalFile(attachment),
          },
      ].filter(Boolean)
    );
  };

  // Função para baixar arquivo local na web
  const downloadLocalFile = attachment => {
    const fileData = localFileData[attachment.id];

    if (fileData && fileData.blob) {
      const url = URL.createObjectURL(fileData.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.name || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else if (fileData && fileData.dataUrl) {
      const a = document.createElement('a');
      a.href = fileData.dataUrl;
      a.download = attachment.name || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      Alert.alert('❌ Erro', 'Arquivo não disponível para download');
    }
  };

  // Função para abrir links
  const openLink = url => {
    let finalUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      finalUrl = 'https://' + url;
    }

    Linking.canOpenURL(finalUrl).then(supported => {
      if (supported) {
        Linking.openURL(finalUrl);
      } else {
        Alert.alert('❌ Erro', 'Não foi possível abrir o link');
      }
    });
  };

  // Função para validar tamanho do arquivo
  const validateFileSize = size => {
    if (size && size > MAX_FILE_SIZE) {
      Alert.alert(
        '❌ Arquivo muito grande',
        `O arquivo selecionado (${(size / 1024 / 1024).toFixed(1)}MB) excede o limite de 10MB.`,
        [{ text: 'OK' }]
      );
      return false;
    }
    return true;
  };

  // Função de formatação de data
  const formatDate = dateString => {
    if (!dateString) return '';

    try {
      // Se já for uma string no formato DD/MM/YYYY, retorna como está
      if (typeof dateString === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
        return dateString;
      }

      // Tenta converter para data
      const date = new Date(dateString);

      // Verifica se é uma data válida
      if (isNaN(date.getTime())) {
        console.warn('❌ Data inválida:', dateString);
        return '';
      }

      // Formata no padrão brasileiro
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('❌ Erro ao formatar data:', error, dateString);
      return '';
    }
  };

  // Função para verificar se a data está vencida
  const isExpired = dateString => {
    if (!dateString) return false;

    try {
      // Converte a string de data para um objeto Date
      let date;

      // Se estiver no formato DD/MM/YYYY
      if (typeof dateString === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
        const [day, month, year] = dateString.split('/').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateString);
      }

      // Verifica se é uma data válida
      if (isNaN(date.getTime())) {
        return false;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    } catch (error) {
      console.error('❌ Erro ao verificar expiração:', error);
      return false;
    }
  };

  // Função para verificar se a data está próxima do vencimento (30 dias)
  const isNearExpiry = dateString => {
    if (!dateString) return false;

    try {
      // Converte a string de data para um objeto Date
      let date;

      // Se estiver no formato DD/MM/YYYY
      if (typeof dateString === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
        const [day, month, year] = dateString.split('/').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateString);
      }

      // Verifica se é uma data válida
      if (isNaN(date.getTime())) {
        return false;
      }

      const today = new Date();
      const diffTime = date - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 30 && diffDays >= 0;
    } catch (error) {
      console.error('❌ Erro ao verificar proximidade de expiração:', error);
      return false;
    }
  };

  // Função de validação de formato de data
  const validateDateFormat = dateString => {
    if (!dateString) return false;

    // Se já for um objeto Date, converte para string no formato DD/MM/YYYY
    if (dateString instanceof Date) {
      const day = String(dateString.getDate()).padStart(2, '0');
      const month = String(dateString.getMonth() + 1).padStart(2, '0');
      const year = dateString.getFullYear();
      dateString = `${day}/${month}/${year}`;
    }

    // Verifica se está no formato DD/MM/YYYY
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    if (!regex.test(dateString)) return false;

    // Extrai dia, mês e ano
    const [, day, month, year] = dateString.match(regex);

    // Verifica se é uma data válida
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return (
      date.getDate() === Number(day) &&
      date.getMonth() === Number(month) - 1 &&
      date.getFullYear() === Number(year)
    );
  };

  // Função para formatar input de data enquanto digita
  const formatDateInput = text => {
    const numbers = text.replace(/\D/g, '');

    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
    }
  };

  // Criar uma data padrão para um ano à frente
  const createDefaultValidityDate = () => {
    const today = new Date();
    const futureDate = new Date(today.getTime() + 365 * 24 * 60 * 60 * 1000); // +1 ano

    // Formata no padrão brasileiro DD/MM/YYYY
    const day = String(futureDate.getDate()).padStart(2, '0');
    const month = String(futureDate.getMonth() + 1).padStart(2, '0');
    const year = futureDate.getFullYear();

    return `${day}/${month}/${year}`;
  };

  // Função para adicionar anexo
  const addAttachment = async newAttachment => {
    // Adicionar campos básicos
    const attachmentWithMeta = {
      ...newAttachment,
      questionId: questionId, // Associar ao ID da pergunta
      synced: false, // Marcar como não sincronizado por padrão
      // Incluir dados locais se existirem
      localData: localFileData[newAttachment.id],
    };

    // Adicionar à lista
    const updatedAttachments = [...attachments, attachmentWithMeta];
    onAttachmentsChange(updatedAttachments);
  };

  // Função para confirmar exclusão
  const confirmRemoveAttachment = attachment => {
    setConfirmDeleteDialog({
      visible: true,
      attachment: attachment,
    });
  };

  // Função para executar exclusão
  const executeRemoveAttachment = async () => {
    const { attachment } = confirmDeleteDialog;
    if (attachment) {
      try {
        // Limpar dados locais se existirem
        if (localFileData[attachment.id]) {
          const newLocalData = { ...localFileData };
          delete newLocalData[attachment.id];
          setLocalFileData(newLocalData);
        }

        // Remover do estado local
        const updatedAttachments = attachments.filter(a => a.id !== attachment.id);
        onAttachmentsChange(updatedAttachments);

        Alert.alert('✅ Sucesso', 'Anexo removido com sucesso!');
      } catch (error) {
        console.error('❌ Erro ao excluir anexo:', error);
        Alert.alert('❌ Erro', 'Erro ao remover o anexo: ' + (error.message || 'Desconhecido'), [
          { text: 'OK' },
        ]);
      }
    }
    setConfirmDeleteDialog({ visible: false, attachment: null });
  };

  // CORREÇÃO: Ação da câmera com melhor processamento de arquivos
  const simpleCameraAction = async () => {
    console.log('🔊 INICIANDO FUNÇÃO SIMPLIFICADA DA CÂMERA');

    if (photoCount >= MAX_PHOTOS) {
      Alert.alert('⚠️ Limite atingido', `Máximo de ${MAX_PHOTOS} fotos por pergunta.`);
      return;
    }

    // Verificar a plataforma e mostrar opções específicas
    if (Platform.OS === 'web') {
      // SOLUÇÃO WEB: Usar input de arquivo para captura
      try {
        console.log('🔊 DETECTADO AMBIENTE WEB');

        // Criar input de arquivo oculto
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*,video/*';
        fileInput.capture = 'camera'; // Tenta abrir a câmera em dispositivos móveis
        fileInput.style.display = 'none';

        // Adicionar à página
        document.body.appendChild(fileInput);

        // Configurar evento para quando um arquivo for selecionado
        fileInput.onchange = async event => {
          const file = event.target.files[0];
          if (file) {
            console.log('🔊 ARQUIVO SELECIONADO:', file.name);

            try {
              // Criar URL temporária e converter para base64
              const url = URL.createObjectURL(file);
              let base64 = null;

              if (file.type.startsWith('image/')) {
                try {
                  base64 = await fileToBase64(file);
                  console.log('✅ Imagem convertida para base64');
                } catch (error) {
                  console.warn('⚠️ Não foi possível converter para base64:', error);
                }
              }

              // Criar objeto de asset
              const asset = {
                url: url,
                uri: url,
                base64: base64,
                name: file.name,
                type: file.type,
                size: file.size,
                file: file, // Manter referência ao arquivo original
              };

              // Abrir diálogo para descrição
              setDescriptionDialog({
                visible: true,
                type: file.type.includes('video') ? 'video' : 'photo',
                file: asset,
                description: '',
                hasValidity: false,
                validityDate: '',
              });

              // Mostrar alerta de sucesso
              Alert.alert(
                '✅ Arquivo selecionado',
                `Nome: ${file.name}\nTipo: ${file.type}\nTamanho: ${(file.size / 1024).toFixed(1)} KB`
              );
            } catch (error) {
              console.error('❌ Erro ao processar arquivo:', error);
              Alert.alert('❌ Erro', 'Não foi possível processar o arquivo selecionado');
            }
          }

          // Remover o input após uso
          document.body.removeChild(fileInput);
        };

        // Simular clique para abrir o seletor de arquivo
        fileInput.click();
      } catch (error) {
        console.error('🔊 ERRO NA CÂMERA WEB:', error);
        Alert.alert(
          '❌ Erro ao acessar câmera',
          'Não foi possível acessar a câmera no navegador. Erro: ' + error.message
        );
      }
    } else {
      // SOLUÇÃO MOBILE: Mostrar menu de opções
      console.log('🔊 DETECTADO AMBIENTE MOBILE');

      Alert.alert('📸 Capturar Foto', 'Escolha uma opção:', [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: '📸 Câmera',
          onPress: async () => {
            try {
              console.log('🔊 TENTANDO ABRIR CÂMERA PARA FOTO');

              // Solicitar permissões explicitamente
              const { status } = await ImagePicker.requestCameraPermissionsAsync();
              console.log('🔊 STATUS PERMISSÃO CÂMERA:', status);

              if (status !== 'granted') {
                Alert.alert(
                  '❌ Permissão Negada',
                  'Você precisa permitir o acesso à câmera para capturar fotos.'
                );
                return;
              }

              // Configuração mínima
              const options = {
                mediaTypes: ImagePicker.MediaTypeOptions.Images,
                quality: 1.0,
                base64: true, // Solicitar base64 no mobile também
              };

              // Abrir câmera
              const result = await ImagePicker.launchCameraAsync(options);
              console.log('🔊 RESULTADO CÂMERA:', result.canceled ? 'CANCELADO' : 'SUCESSO');

              if (!result.canceled && result.assets && result.assets.length > 0) {
                const asset = result.assets[0];

                // Adicionar base64 se disponível
                if (result.assets[0].base64) {
                  asset.base64 = `data:image/jpeg;base64,${result.assets[0].base64}`;
                }

                // Abrir diálogo de descrição
                setDescriptionDialog({
                  visible: true,
                  type: 'photo',
                  file: asset,
                  description: '',
                  hasValidity: false,
                  validityDate: '',
                });
              }
            } catch (error) {
              console.error('🔊 ERRO NA CÂMERA:', error);
              Alert.alert('❌ Erro', 'Não foi possível abrir a câmera: ' + error.message);
            }
          },
        },
        {
          text: '📱 Galeria',
          onPress: async () => {
            try {
              console.log('🔊 TENTANDO ABRIR GALERIA');

              // Solicitar permissões explicitamente
              const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
              console.log('🔊 STATUS PERMISSÃO GALERIA:', status);

              if (status !== 'granted') {
                Alert.alert(
                  '❌ Permissão Negada',
                  'Você precisa permitir o acesso à galeria para selecionar fotos.'
                );
                return;
              }

              // Configuração mínima
              const options = {
                mediaTypes: ImagePicker.MediaTypeOptions.All,
                quality: 0.8,
                base64: true, // Solicitar base64 no mobile também
              };

              // Abrir galeria
              const result = await ImagePicker.launchImageLibraryAsync(options);
              console.log('🔊 RESULTADO GALERIA:', result.canceled ? 'CANCELADO' : 'SUCESSO');

              if (!result.canceled && result.assets && result.assets.length > 0) {
                const asset = result.assets[0];

                // Adicionar base64 se disponível
                if (result.assets[0].base64) {
                  asset.base64 = `data:${asset.type || 'image/jpeg'};base64,${result.assets[0].base64}`;
                }

                // Determinar tipo (foto ou vídeo)
                const isVideo =
                  asset.type === 'video' ||
                  (asset.url &&
                    (asset.url.toLowerCase().endsWith('.mp4') ||
                      asset.url.toLowerCase().endsWith('.mov') ||
                      asset.url.toLowerCase().endsWith('.avi')));

                // Abrir diálogo de descrição
                setDescriptionDialog({
                  visible: true,
                  type: isVideo ? 'video' : 'photo',
                  file: asset,
                  description: '',
                  hasValidity: false,
                  validityDate: '',
                });
              }
            } catch (error) {
              console.error('🔊 ERRO NA GALERIA:', error);
              Alert.alert('❌ Erro', 'Não foi possível abrir a galeria: ' + error.message);
            }
          },
        },
      ]);
    }
  };

  // Função para anexar arquivo
  const handleFileAttachment = async () => {
    if (fileCount >= MAX_FILES) {
      Alert.alert('⚠️ Limite atingido', `Máximo de ${MAX_FILES} arquivos por pergunta.`);
      return;
    }

    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        if (!validateFileSize(asset.size)) return;

        // CORREÇÃO: Garantir que o asset tenha uma URL
        if (!asset.url && asset.uri) {
          asset.url = asset.uri;
        }

        setDescriptionDialog({
          visible: true,
          type: 'file',
          file: asset,
          description: '',
          hasValidity: false,
          validityDate: '',
        });
      }
    } catch (error) {
      console.error('❌ Erro ao selecionar arquivo:', error);
      Alert.alert('❌ Erro', 'Erro ao selecionar arquivo: ' + (error.message || 'Desconhecido'));
    }
  };

  // Função para adicionar link
  const handleLinkAttachment = () => {
    if (linkCount >= MAX_LINKS) {
      Alert.alert('⚠️ Limite atingido', `Máximo de ${MAX_LINKS} links por pergunta.`);
      return;
    }

    setShowLinkDialog(true);
    setLinkUrl('');
    setLinkDescription('');
    setLinkHasValidity(false);
    setLinkValidityDate('');
  };

  // Função para salvar link
  const saveLinkAttachment = async () => {
    if (!linkUrl.trim()) {
      Alert.alert('⚠️ Campo obrigatório', 'Digite um link válido.');
      return;
    }

    if (!linkDescription.trim() || linkDescription.trim().length < 3) {
      Alert.alert('⚠️ Descrição obrigatória', 'Digite pelo menos 3 caracteres de descrição.');
      return;
    }

    if (linkHasValidity && !validateDateFormat(linkValidityDate)) {
      Alert.alert(
        '⚠️ Data inválida',
        'Você indicou que este link tem data de validade.\n\nPor favor, digite uma data válida no formato DD/MM/AAAA.'
      );
      return;
    }

    const newAttachment = {
      id: generateId(),
      type: 'link',
      url: linkUrl.trim(),
      description: linkDescription.trim(),
      validityDate: linkHasValidity ? linkValidityDate : null,
      createdAt: new Date().toISOString(),
      synced: false,
      questionId: questionId,
    };

    // Adicionar à lista
    addAttachment(newAttachment);

    // Limpar campos
    setLinkUrl('');
    setLinkDescription('');
    setLinkHasValidity(false);
    setLinkValidityDate('');
    setShowLinkDialog(false);

    Alert.alert('✅ Sucesso', 'Link anexado com sucesso!');
  };

  // CORREÇÃO PRINCIPAL: Salvar anexo com descrição e dados locais
  const saveAttachmentWithDescription = async () => {
  if (!descriptionDialog.description.trim() || descriptionDialog.description.trim().length < 3) {
    Alert.alert('⚠️ Descrição obrigatória', 'Digite pelo menos 3 caracteres de descrição.');
    return;
  }

  if (descriptionDialog.hasValidity && !validateDateFormat(descriptionDialog.validityDate)) {
    Alert.alert(
      '⚠️ Data inválida',
      'Você indicou que esta evidência tem data de validade.\n\nPor favor, digite uma data válida no formato DD/MM/AAAA.'
    );
    return;
  }

  const { type, file } = descriptionDialog;

  try {
    setIsUploading(true);
    
    // IMPORTANTE: Converter arquivo para base64 para salvar localmente
    let base64Data = file.base64;
    
    // Se não tem base64, tentar converter
    if (!base64Data && file.url) {
      try {
        if (Platform.OS === 'web' && file.file) {
          // Na web, converter File/Blob para base64
          base64Data = await fileToBase64(file.file);
        } else if (file.uri) {
          // No mobile, o base64 já deve ter vindo do ImagePicker
          // Se não veio, marcar para conversão futura
          console.log('⚠️ Mobile: arquivo sem base64, salvando URI');
        }
      } catch (conversionError) {
        console.warn('⚠️ Não foi possível converter para base64:', conversionError);
      }
    }

    // Criar objeto de anexo com dados completos
    const newAttachmentId = generateId();
    const newAttachment = {
      id: newAttachmentId,
      type: type,
      url: file.url || file.uri,
      uri: file.uri || file.url,
      base64: base64Data, // CRÍTICO: Salvar base64 para persistência
      name: file.name || `${type}_${Date.now()}`,
      size: file.fileSize || file.size,
      mimeType: file.mimeType || file.type || 'application/octet-stream',
      description: descriptionDialog.description.trim(),
      validityDate: descriptionDialog.hasValidity ? descriptionDialog.validityDate : null,
      createdAt: new Date().toISOString(),
      synced: false,
      localOnly: true,
      questionId: questionId,
      // NOVO: Adicionar flag para indicar que tem dados locais
      hasLocalData: !!base64Data,
    };

    console.log('📎 Anexo preparado para salvar:', {
      id: newAttachment.id,
      name: newAttachment.name,
      hasBase64: !!newAttachment.base64,
      base64Length: newAttachment.base64 ? newAttachment.base64.length : 0,
    });

    // Adicionar o anexo
    addAttachment(newAttachment);

    // Fechar o diálogo
    setDescriptionDialog({
      visible: false,
      type: '',
      file: null,
      description: '',
      hasValidity: false,
      validityDate: '',
    });

    Alert.alert(
      '✅ Sucesso',
      `${type === 'photo' ? 'Foto' : type === 'video' ? 'Vídeo' : 'Arquivo'} anexado com sucesso.`,
      [{ text: 'OK' }]
    );
  } catch (error) {
    console.error('❌ Erro ao processar anexo:', error);
    Alert.alert(
      '❌ Erro no processamento',
      `Não foi possível processar o arquivo.\n\nErro: ${error.message || 'Desconhecido'}`,
      [{ text: 'OK' }]
    );
  } finally {
    setIsUploading(false);
  }
};  

  // Editar data de validade
  const editValidityDate = attachment => {
    // Garantir que a data de validade seja exibida no formato correto
    let formattedDate = '';

    if (attachment.validityDate) {
      // Tenta formatar a data usando nossa função aprimorada
      formattedDate = formatDate(attachment.validityDate);

      // Se não conseguir formatar corretamente, usa uma string vazia
      if (formattedDate === 'Invalid Date' || !formattedDate) {
        console.warn('⚠️ Data inválida detectada, resetando:', attachment.validityDate);
        formattedDate = '';
      }
    }

    // Configura o diálogo com a data formatada
    setEditValidityDialog({
      visible: true,
      attachment: attachment,
      newDate: formattedDate,
    });
  };

  // Função para salvar nova data de validade
  const saveNewValidityDate = async () => {
    const { attachment, newDate } = editValidityDialog;

    if (!newDate || newDate.trim() === '') {
      // Remover data de validade
      const updatedAttachment = {
        ...attachment,
        validityDate: null,
      };

      // Atualizar no array de anexos
      const updatedAttachments = attachments.map(a =>
        a.id === attachment.id ? updatedAttachment : a
      );
      onAttachmentsChange(updatedAttachments);

      setEditValidityDialog({ visible: false, attachment: null, newDate: '' });
      Alert.alert('✅ Sucesso', 'Data de validade removida.');
    } else if (validateDateFormat(newDate.trim())) {
      // Atualizar data de validade com a string já formatada
      const updatedAttachment = {
        ...attachment,
        validityDate: newDate.trim(),
      };

      // Atualizar no array de anexos
      const updatedAttachments = attachments.map(a =>
        a.id === attachment.id ? updatedAttachment : a
      );
      onAttachmentsChange(updatedAttachments);

      setEditValidityDialog({ visible: false, attachment: null, newDate: '' });
      Alert.alert('✅ Sucesso', 'Data de validade atualizada.');
    } else {
      Alert.alert('❌ Erro', 'Data inválida. Use o formato DD/MM/AAAA.');
    }
  };

  // Função para formatar tamanho
  const formatFileSize = bytes => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Verificar se o texto tem pelo menos 3 caracteres
  const hasMinimumCharacters = text => {
    return text && text.trim().length >= 3;
  };

  // Função para verificar se pode salvar link
  const canSaveLinkAttachment = () => {
    // Precisa ter URL
    if (!linkUrl.trim()) return false;

    // Precisa ter descrição com pelo menos 3 caracteres
    if (!hasMinimumCharacters(linkDescription)) return false;

    // Se marcou que tem data de validade, precisa ter uma data válida
    if (linkHasValidity && !validateDateFormat(linkValidityDate)) return false;

    return true;
  };

  // Função para verificar se pode salvar anexo com descrição
  const canSaveAttachmentWithDescription = () => {
    // Precisa ter descrição com pelo menos 3 caracteres
    if (!hasMinimumCharacters(descriptionDialog.description)) return false;

    // Se marcou que tem data de validade, precisa ter uma data válida
    if (descriptionDialog.hasValidity && !validateDateFormat(descriptionDialog.validityDate))
      return false;

    return true;
  };

  return (
    <View style={styles.container}>
      {/* Botões de Ação */}
      <View style={styles.actionButtons}>
        {/* BOTÃO DE CÂMERA */}
        <TouchableOpacity
          onPress={simpleCameraAction}
          style={{
            backgroundColor: disabled || isUploading ? '#CCCCCC' : '#FFFFFF',
            padding: 10,
            borderRadius: 4,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 8,
            borderWidth: 1,
            borderColor: disabled || isUploading ? '#CCCCCC' : AVAQualisTheme.colors.primary,
            flex: 1,
          }}
          disabled={disabled || isUploading || photoCount >= MAX_PHOTOS}
        >
          <Text
            style={{
              color: disabled || isUploading ? '#999' : AVAQualisTheme.colors.primary,
              fontWeight: 'bold',
              fontSize: 16,
            }}
          >
            📸 Foto {photoCount > 0 && `(${photoCount})`}
          </Text>
        </TouchableOpacity>

        {/* BOTÃO DE ANEXAR ARQUIVO */}
        <TouchableOpacity
          onPress={handleFileAttachment}
          style={{
            backgroundColor: disabled || isUploading ? '#CCCCCC' : '#FFFFFF',
            padding: 10,
            borderRadius: 4,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 8,
            borderWidth: 1,
            borderColor: disabled || isUploading ? '#CCCCCC' : AVAQualisTheme.colors.primary,
            flex: 1,
          }}
          disabled={disabled || isUploading || fileCount >= MAX_FILES}
        >
          <Text
            style={{
              color: disabled || isUploading ? '#999' : AVAQualisTheme.colors.primary,
              fontWeight: 'bold',
              fontSize: 16,
            }}
          >
            📎 Anexar {fileCount > 0 && `(${fileCount})`}
          </Text>
        </TouchableOpacity>

        {/* BOTÃO DE LINK */}
        <TouchableOpacity
          onPress={handleLinkAttachment}
          style={{
            backgroundColor: disabled || isUploading ? '#CCCCCC' : '#FFFFFF',
            padding: 10,
            borderRadius: 4,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 1,
            borderColor: disabled || isUploading ? '#CCCCCC' : AVAQualisTheme.colors.primary,
            flex: 1,
          }}
          disabled={disabled || isUploading || linkCount >= MAX_LINKS}
        >
          <Text
            style={{
              color: disabled || isUploading ? '#999' : AVAQualisTheme.colors.primary,
              fontWeight: 'bold',
              fontSize: 16,
            }}
          >
            🔗 Link {linkCount > 0 && `(${linkCount})`}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Lista de Anexos */}
      {attachments.length > 0 && (
        <View style={styles.attachmentsList}>
          <Text style={styles.attachmentsTitle}>📎 Anexos ({attachments.length})</Text>

          {attachments.map(attachment => (
            <TouchableOpacity
              key={attachment.id}
              style={[
                styles.attachmentCard,
                hoveredAttachment === attachment.id && styles.attachmentCardHovered,
              ]}
              onPress={() => handleViewAttachment(attachment)}
              onMouseEnter={() => setHoveredAttachment(attachment.id)}
              onMouseLeave={() => setHoveredAttachment(null)}
              activeOpacity={0.7}
            >
              <Card.Content style={styles.attachmentContent}>
                <View style={styles.attachmentHeader}>
                  <View style={styles.attachmentInfo}>
                    {/* Ícone/Thumbnail */}
                    <View style={styles.attachmentIcon}>
                      {attachment.type === 'photo' &&
                      (attachment.url ||
                        attachment.base64 ||
                        localFileData[attachment.id]?.dataUrl) ? (
                        <Image
                          source={{
                            uri:
                              localFileData[attachment.id]?.dataUrl ||
                              attachment.base64 ||
                              attachment.url,
                          }}
                          style={styles.thumbnailImage}
                        />
                      ) : attachment.type === 'video' ? (
                        <View style={styles.videoThumbnail}>
                          <Text style={styles.videoIcon}>🎥</Text>
                        </View>
                      ) : attachment.type === 'link' ? (
                        <View style={styles.linkIcon}>
                          <Text style={styles.linkIconText}>🔗</Text>
                        </View>
                      ) : (
                        <Text style={styles.fileIcon}>
                          {getFileEmoji(attachment.mimeType, attachment.name)}
                        </Text>
                      )}

                      {/* Indicador de Supabase */}
                      {attachment.supabaseUrl && (
                        <View style={styles.supabaseBadge}>
                          <Text style={styles.supabaseBadgeText}>🌐</Text>
                        </View>
                      )}

                      {/* Indicador de local only */}
                      {attachment.localOnly && (
                        <View style={styles.localOnlyBadge}>
                          <Text style={styles.localOnlyBadgeText}>📱</Text>
                        </View>
                      )}

                      {/* Indicador de MongoDB */}
                      {attachment.synced && (
                        <View style={styles.mongoBadge}>
                          <Text style={styles.mongoBadgeText}>📊</Text>
                        </View>
                      )}

                      {/* Overlay de lupa - Aparece no hover */}
                      {hoveredAttachment === attachment.id && (
                        <View style={styles.hoverOverlay}>
                          <Text style={styles.hoverIcon}>🔍</Text>
                          <Text style={styles.hoverText}>Visualizar</Text>
                        </View>
                      )}
                    </View>

                    {/* Informações do anexo */}
                    <View style={styles.attachmentDetails}>
                      <Text style={styles.attachmentName} numberOfLines={1}>
                        {attachment.type === 'link'
                          ? attachment.url
                          : attachment.name || 'Arquivo sem nome'}
                      </Text>

                      {attachment.size && (
                        <Text style={styles.attachmentSize}>
                          📏 {formatFileSize(attachment.size)}
                        </Text>
                      )}

                      <Text style={styles.attachmentDescription} numberOfLines={2}>
                        {attachment.description}
                      </Text>

                      {/* Mostrar data de validade se existir */}
                      {attachment.validityDate && (
                        <Text
                          style={[
                            styles.attachmentValidity,
                            isExpired(attachment.validityDate) && styles.attachmentValidityExpired,
                            isNearExpiry(attachment.validityDate) &&
                              styles.attachmentValidityWarning,
                          ]}
                        >
                          📅 Válido até: {formatDate(attachment.validityDate)}
                          {isExpired(attachment.validityDate) && ' (VENCIDO)'}
                          {isNearExpiry(attachment.validityDate) &&
                            !isExpired(attachment.validityDate) &&
                            ' (PRÓXIMO DO VENCIMENTO)'}
                        </Text>
                      )}

                      {/* Status de sincronização MongoDB */}
                      <Text style={styles.syncStatus}>
                        {attachment.synced
                          ? `☁️ Sincronizado: ${formatDate(attachment.syncedAt || attachment.createdAt)}`
                          : '⏳ Aguardando envio na tela de Upload'}
                      </Text>
                    </View>
                  </View>

                  {/* Botões de Ação */}
                  <View style={styles.attachmentActions}>
                    {/* Lupa principal - Sempre visível */}
                    <IconButton
                      icon="magnify"
                      size={24}
                      onPress={() => handleViewAttachment(attachment)}
                      iconColor={
                        hoveredAttachment === attachment.id
                          ? 'white'
                          : AVAQualisTheme.colors.primary
                      }
                      style={[
                        styles.viewButtonMain,
                        hoveredAttachment === attachment.id && styles.viewButtonMainHovered,
                      ]}
                    />

                    {/* Botão para editar data de validade */}
                    <IconButton
                      icon="calendar-edit"
                      size={18}
                      onPress={() => editValidityDate(attachment)}
                      iconColor={AVAQualisTheme.colors.tertiary}
                      disabled={disabled || isUploading}
                      style={styles.secondaryActionButton}
                    />

                    {/* Botão de exclusão */}
                    <IconButton
                      icon="delete-outline"
                      size={18}
                      onPress={() => confirmRemoveAttachment(attachment)}
                      iconColor={AVAQualisTheme.colors.error}
                      disabled={disabled || isUploading}
                      style={styles.secondaryActionButton}
                    />
                  </View>
                </View>

                {/* Preview tooltip - Aparece no hover para arquivos */}
                {hoveredAttachment === attachment.id && attachment.type !== 'link' && (
                  <View style={styles.previewTooltip}>
                    <Text style={styles.tooltipText}>
                      🔍 Clique para visualizar este{' '}
                      {attachment.type === 'photo'
                        ? 'foto'
                        : attachment.type === 'video'
                          ? 'vídeo'
                          : 'arquivo'}
                    </Text>
                    {attachment.type !== 'photo' && attachment.type !== 'video' && (
                      <Text style={styles.tooltipSubtext}>
                        Tipo: {attachment.mimeType || 'Desconhecido'}
                      </Text>
                    )}
                  </View>
                )}
              </Card.Content>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* Dialog de Adição de Link */}
      <Modal
        visible={showLinkDialog}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowLinkDialog(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowLinkDialog(false)}
        >
          <TouchableOpacity style={styles.modalContent} activeOpacity={1} onPress={() => {}}>
            <Text style={styles.modalTitle}>🔗 Adicionar Link</Text>

            <Text style={styles.modalLabel}>URL do Link:</Text>
            <TextInput
              style={styles.modalInput}
              placeholder="Digite ou cole o link (ex: https://site.com.br)"
              value={linkUrl}
              onChangeText={setLinkUrl}
              autoCapitalize="none"
              keyboardType="url"
              placeholderTextColor="#999"
            />

            <Text style={styles.modalLabel}>Descrição *:</Text>
            <TextInput
              style={[styles.modalInput, styles.modalInputMultiline]}
              placeholder="Descreva o que este link contém... (mínimo de 3 caracteres)"
              value={linkDescription}
              onChangeText={setLinkDescription}
              multiline={true}
              numberOfLines={3}
              placeholderTextColor="#999"
            />

            <Text
              style={[
                styles.modalCharCount,
                hasMinimumCharacters(linkDescription)
                  ? { color: AVAQualisTheme.colors.success }
                  : linkDescription.length > 0
                    ? { color: AVAQualisTheme.colors.error }
                    : {},
              ]}
            >
              {linkDescription.length} caracteres{' '}
              {!hasMinimumCharacters(linkDescription) && '(mínimo 3)'}
              {hasMinimumCharacters(linkDescription) && ' ✅'}
            </Text>

            {/* Seção de data de validade */}
            <View style={styles.validitySection}>
              <TouchableOpacity
                style={styles.validityToggle}
                onPress={() => {
                  // Se já está marcado e tem data preenchida, pedir confirmação
                  if (linkHasValidity && linkValidityDate) {
                    Alert.alert(
                      '📅 Remover Data de Validade?',
                      'Deseja realmente remover a data de validade deste link?',
                      [
                        { text: 'Cancelar', style: 'cancel' },
                        {
                          text: 'Remover',
                          onPress: () => {
                            setLinkHasValidity(false);
                            setLinkValidityDate('');
                          },
                        },
                      ]
                    );
                  } else {
                    // Se estiver marcando, colocar data padrão do dia atual
                    if (!linkHasValidity) {
                      setLinkHasValidity(true);
                      setLinkValidityDate(createDefaultValidityDate());
                    } else {
                      setLinkHasValidity(false);
                      setLinkValidityDate('');
                    }
                  }
                }}
              >
                <View
                  style={[
                    styles.validityCheckbox,
                    linkHasValidity && styles.validityCheckboxChecked,
                  ]}
                >
                  {linkHasValidity && <Text style={styles.validityCheckmark}>✓</Text>}
                </View>
                <Text style={styles.validityToggleText}>📅 Este link tem data de validade</Text>
              </TouchableOpacity>

              {linkHasValidity && (
                <>
                  <View style={styles.dateInputContainer}>
                    <TextInput
                      style={[
                        styles.dateInput,
                        !validateDateFormat(linkValidityDate) && {
                          borderColor: AVAQualisTheme.colors.error,
                          borderWidth: 2,
                        },
                      ]}
                      placeholder="DD/MM/AAAA"
                      value={linkValidityDate}
                      onChangeText={text => setLinkValidityDate(formatDateInput(text))}
                      keyboardType="numeric"
                      maxLength={10}
                      placeholderTextColor="#999"
                    />
                    <TouchableOpacity
                      style={styles.calendarButton}
                      onPress={() => {
                        setLinkValidityDate(createDefaultValidityDate());
                      }}
                    >
                      <Text style={styles.calendarButtonText}>📅</Text>
                    </TouchableOpacity>
                  </View>

                  {/* Mensagem de feedback sobre a validação da data */}
                  <Text
                    style={[
                      styles.modalCharCount,
                      validateDateFormat(linkValidityDate)
                        ? { color: AVAQualisTheme.colors.success, marginTop: 5 }
                        : { color: AVAQualisTheme.colors.error, marginTop: 5 },
                    ]}
                  >
                    {validateDateFormat(linkValidityDate)
                      ? `✅ Data válida: ${linkValidityDate}`
                      : `⚠️ Data obrigatória no formato DD/MM/AAAA`}
                  </Text>
                </>
              )}
            </View>

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.modalButtonCancel}
                onPress={() => setShowLinkDialog(false)}
              >
                <Text style={[styles.modalButtonText, styles.modalButtonTextCancel]}>Cancelar</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.modalButtonSave,
                  !canSaveLinkAttachment() && styles.modalButtonDisabled,
                ]}
                onPress={saveLinkAttachment}
                disabled={!canSaveLinkAttachment() || isUploading}
              >
                <Text
                  style={[
                    styles.modalButtonText,
                    styles.modalButtonTextSave,
                    !canSaveLinkAttachment() && { opacity: 0.5 },
                  ]}
                >
                  Salvar Link
                </Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Dialog de Confirmação de Exclusão */}
      <Modal
        visible={confirmDeleteDialog.visible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setConfirmDeleteDialog({ visible: false, attachment: null })}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setConfirmDeleteDialog({ visible: false, attachment: null })}
        >
          <TouchableOpacity style={styles.modalContent} activeOpacity={1} onPress={() => {}}>
            <View style={styles.confirmationDialog}>
              <Text style={styles.confirmationIcon}>🗑️</Text>
              <Text style={styles.modalTitle}>Confirmar Exclusão</Text>
              <Text style={styles.confirmationMessage}>
                Deseja realmente remover este anexo?
                {confirmDeleteDialog.attachment &&
                  `\n\n${
                    confirmDeleteDialog.attachment.type === 'link'
                      ? confirmDeleteDialog.attachment.url
                      : confirmDeleteDialog.attachment.name || 'Arquivo'
                  }`}
              </Text>

              <View style={styles.confirmationButtons}>
                <TouchableOpacity
                  style={styles.confirmationButtonCancel}
                  onPress={() => setConfirmDeleteDialog({ visible: false, attachment: null })}
                >
                  <Text style={[styles.modalButtonText, styles.modalButtonTextCancel]}>
                    Cancelar
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.confirmationButtonDelete}
                  onPress={executeRemoveAttachment}
                  disabled={isUploading}
                >
                  <Text style={[styles.modalButtonText, styles.modalButtonTextSave]}>
                    🗑️ Excluir
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Dialog para Descrição */}
      <Modal
        visible={descriptionDialog.visible}
        transparent={true}
        animationType="fade"
        onRequestClose={() =>
          setDescriptionDialog({
            visible: false,
            type: '',
            file: null,
            description: '',
            hasValidity: false,
            validityDate: '',
          })
        }
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() =>
            setDescriptionDialog({
              visible: false,
              type: '',
              file: null,
              description: '',
              hasValidity: false,
              validityDate: '',
            })
          }
        >
          <TouchableOpacity style={styles.modalContent} activeOpacity={1} onPress={() => {}}>
            <Text style={styles.modalTitle}>
              {descriptionDialog.type === 'photo'
                ? '📸 Descrever Foto'
                : descriptionDialog.type === 'video'
                  ? '🎥 Descrever Vídeo'
                  : '📎 Descrever Arquivo'}
            </Text>

            {descriptionDialog.file && (
              <Text style={[styles.modalLabel, { textAlign: 'center', marginBottom: 15 }]}>
                📄 {descriptionDialog.file.name || 'Arquivo capturado'}
              </Text>
            )}

            <Text style={styles.modalLabel}>Descrição da Evidência *:</Text>
            <TextInput
              mode="outlined"
              multiline
              numberOfLines={4}
              placeholder="Descreva o que esta evidência comprova... (mínimo de 3 caracteres)"
              placeholderTextColor="#999"
              value={descriptionDialog.description}
              onChangeText={text =>
                setDescriptionDialog({
                  ...descriptionDialog,
                  description: text,
                })
              }
              style={{
                borderWidth: 1,
                borderColor: AVAQualisTheme.colors.outline,
                borderRadius: 4,
                padding: 8,
                minHeight: 100,
                textAlignVertical: 'top',
              }}
              outlineColor={AVAQualisTheme.colors.outline}
              activeOutlineColor={AVAQualisTheme.colors.primary}
            />

            <Text
              style={[
                styles.modalCharCount,
                hasMinimumCharacters(descriptionDialog.description)
                  ? { color: AVAQualisTheme.colors.success }
                  : descriptionDialog.description.length > 0
                    ? { color: AVAQualisTheme.colors.error }
                    : {},
              ]}
            >
              {descriptionDialog.description.length} caracteres{' '}
              {!hasMinimumCharacters(descriptionDialog.description) && '(mínimo 3)'}
              {hasMinimumCharacters(descriptionDialog.description) && ' ✅'}
            </Text>

            {/* Seção de data de validade */}
            <View style={styles.validitySection}>
              <TouchableOpacity
                style={styles.validityToggle}
                onPress={() => {
                  // Se já está marcado e tem data preenchida, pedir confirmação
                  if (descriptionDialog.hasValidity && descriptionDialog.validityDate) {
                    Alert.alert(
                      '📅 Remover Data de Validade?',
                      'Deseja realmente remover a data de validade desta evidência?',
                      [
                        { text: 'Cancelar', style: 'cancel' },
                        {
                          text: 'Remover',
                          onPress: () => {
                            setDescriptionDialog({
                              ...descriptionDialog,
                              hasValidity: false,
                              validityDate: '',
                            });
                          },
                        },
                      ]
                    );
                  } else {
                    // Se estiver marcando, colocar data padrão do dia atual
                    if (!descriptionDialog.hasValidity) {
                      setDescriptionDialog({
                        ...descriptionDialog,
                        hasValidity: true,
                        validityDate: createDefaultValidityDate(),
                      });
                    } else {
                      setDescriptionDialog({
                        ...descriptionDialog,
                        hasValidity: false,
                        validityDate: '',
                      });
                    }
                  }
                }}
              >
                <View
                  style={[
                    styles.validityCheckbox,
                    descriptionDialog.hasValidity && styles.validityCheckboxChecked,
                  ]}
                >
                  {descriptionDialog.hasValidity && <Text style={styles.validityCheckmark}>✓</Text>}
                </View>
                <Text style={styles.validityToggleText}>
                  📅 Esta evidência tem data de validade
                </Text>
              </TouchableOpacity>

              {descriptionDialog.hasValidity && (
                <>
                  <View style={styles.dateInputContainer}>
                    <TextInput
                      style={[
                        styles.dateInput,
                        !validateDateFormat(descriptionDialog.validityDate) && {
                          borderColor: AVAQualisTheme.colors.error,
                          borderWidth: 2,
                        },
                      ]}
                      placeholder="DD/MM/AAAA"
                      value={descriptionDialog.validityDate}
                      onChangeText={text =>
                        setDescriptionDialog({
                          ...descriptionDialog,
                          validityDate: formatDateInput(text),
                        })
                      }
                      keyboardType="numeric"
                      maxLength={10}
                      placeholderTextColor="#999"
                    />
                    <TouchableOpacity
                      style={styles.calendarButton}
                      onPress={() => {
                        setDescriptionDialog({
                          ...descriptionDialog,
                          validityDate: createDefaultValidityDate(),
                        });
                      }}
                    >
                      <Text style={styles.calendarButtonText}>📅</Text>
                    </TouchableOpacity>
                  </View>

                  {/* Mensagem de feedback sobre a validação da data */}
                  <Text
                    style={[
                      styles.modalCharCount,
                      validateDateFormat(descriptionDialog.validityDate)
                        ? { color: AVAQualisTheme.colors.success, marginTop: 5 }
                        : { color: AVAQualisTheme.colors.error, marginTop: 5 },
                    ]}
                  >
                    {validateDateFormat(descriptionDialog.validityDate)
                      ? `✅ Data válida: ${descriptionDialog.validityDate}`
                      : `⚠️ Data obrigatória no formato DD/MM/AAAA`}
                  </Text>
                </>
              )}
            </View>

            {/* Mensagem informativa sobre envio */}
            <View style={styles.noteContainer}>
              <Text style={styles.noteText}>
                📝 Os anexos ficarão disponíveis apenas localmente até que você envie o formulário
                através da tela de "Pasta de Upload".
              </Text>
            </View>

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.modalButtonCancel}
                onPress={() =>
                  setDescriptionDialog({
                    visible: false,
                    type: '',
                    file: null,
                    description: '',
                    hasValidity: false,
                    validityDate: '',
                  })
                }
                disabled={isUploading}
              >
                <Text style={[styles.modalButtonText, styles.modalButtonTextCancel]}>Cancelar</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.modalButtonSave,
                  (!canSaveAttachmentWithDescription() || isUploading) &&
                    styles.modalButtonDisabled,
                ]}
                onPress={saveAttachmentWithDescription}
                disabled={!canSaveAttachmentWithDescription() || isUploading}
              >
                <Text
                  style={[
                    styles.modalButtonText,
                    styles.modalButtonTextSave,
                    (!canSaveAttachmentWithDescription() || isUploading) && { opacity: 0.5 },
                  ]}
                >
                  {isUploading ? '⏳ Processando...' : 'Salvar Anexo'}
                </Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Dialog para Editar Data de Validade */}
      <Modal
        visible={editValidityDialog.visible}
        transparent={true}
        animationType="fade"
        onRequestClose={() =>
          setEditValidityDialog({ visible: false, attachment: null, newDate: '' })
        }
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setEditValidityDialog({ visible: false, attachment: null, newDate: '' })}
        >
          <TouchableOpacity
            style={[styles.modalContent, styles.smallModalContent]}
            activeOpacity={1}
            onPress={() => {}}
          >
            <Text style={styles.modalTitle}>📅 Editar Data de Validade</Text>

            <Text style={[styles.modalLabel, { textAlign: 'center', marginBottom: 10 }]}>
              {editValidityDialog.attachment?.type === 'link'
                ? editValidityDialog.attachment?.url
                : editValidityDialog.attachment?.name || 'Anexo'}
            </Text>

            <Text style={styles.modalLabel}>
              Data de Validade:
              <Text style={{ fontSize: 12 }}> (deixe em branco para remover)</Text>
            </Text>

            <View style={styles.dateInputContainer}>
              <TextInput
                style={[
                  styles.dateInput,
                  editValidityDialog.newDate &&
                    !validateDateFormat(editValidityDialog.newDate) && {
                      borderColor: AVAQualisTheme.colors.error,
                      borderWidth: 2,
                    },
                ]}
                placeholder="DD/MM/AAAA"
                value={editValidityDialog.newDate}
                onChangeText={text =>
                  setEditValidityDialog({
                    ...editValidityDialog,
                    newDate: formatDateInput(text),
                  })
                }
                keyboardType="numeric"
                maxLength={10}
                placeholderTextColor="#999"
              />
              <TouchableOpacity
                style={styles.calendarButton}
                onPress={() => {
                  setEditValidityDialog({
                    ...editValidityDialog,
                    newDate: createDefaultValidityDate(),
                  });
                }}
              >
                <Text style={styles.calendarButtonText}>📅</Text>
              </TouchableOpacity>
            </View>

            {/* Mensagem de validação */}
            {editValidityDialog.newDate && (
              <Text
                style={[
                  styles.modalCharCount,
                  validateDateFormat(editValidityDialog.newDate)
                    ? { color: AVAQualisTheme.colors.success, marginTop: 5 }
                    : { color: AVAQualisTheme.colors.error, marginTop: 5 },
                ]}
              >
                {validateDateFormat(editValidityDialog.newDate)
                  ? `✅ Data válida: ${editValidityDialog.newDate}`
                  : `⚠️ Formato inválido. Use DD/MM/AAAA`}
              </Text>
            )}

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.modalButtonCancel}
                onPress={() =>
                  setEditValidityDialog({ visible: false, attachment: null, newDate: '' })
                }
              >
                <Text style={[styles.modalButtonText, styles.modalButtonTextCancel]}>Cancelar</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.modalButtonSave,
                  editValidityDialog.newDate &&
                    !validateDateFormat(editValidityDialog.newDate) &&
                    styles.modalButtonDisabled,
                ]}
                onPress={saveNewValidityDate}
                disabled={
                  (editValidityDialog.newDate && !validateDateFormat(editValidityDialog.newDate)) ||
                  isUploading
                }
              >
                <Text
                  style={[
                    styles.modalButtonText,
                    styles.modalButtonTextSave,
                    editValidityDialog.newDate &&
                      !validateDateFormat(editValidityDialog.newDate) && { opacity: 0.5 },
                  ]}
                >
                  Salvar
                </Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Modal de Preview de Imagem */}
      <Modal
        visible={imagePreviewModal.visible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setImagePreviewModal({ visible: false, imageUrl: '', imageName: '' })}
      >
        <TouchableOpacity
          style={{
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          activeOpacity={1}
          onPress={() => setImagePreviewModal({ visible: false, imageUrl: '', imageName: '' })}
        >
          <View
            style={{
              width: '90%',
              maxHeight: '80%',
              backgroundColor: 'white',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            {/* Header do Modal */}
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 16,
                backgroundColor: AVAQualisTheme.colors.primary,
              }}
            >
              <Text
                style={{
                  color: 'white',
                  fontSize: 16,
                  fontWeight: 'bold',
                  flex: 1,
                  marginRight: 8,
                }}
                numberOfLines={1}
              >
                📸 {imagePreviewModal.imageName}
              </Text>
              <TouchableOpacity
                onPress={() =>
                  setImagePreviewModal({ visible: false, imageUrl: '', imageName: '' })
                }
                style={{ padding: 4 }}
              >
                <Text style={{ color: 'white', fontSize: 20 }}>✕</Text>
              </TouchableOpacity>
            </View>

            {/* Imagem */}
            <View
              style={{
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f0f0f0',
              }}
            >
              <Image
                source={{ uri: imagePreviewModal.imageUrl }}
                style={{
                  width: '100%',
                  height: undefined,
                  aspectRatio: 1,
                  resizeMode: 'contain',
                }}
                onError={error => {
                  console.error('❌ Erro ao carregar imagem:', error);
                  Alert.alert('❌ Erro', 'Não foi possível carregar a imagem.', [
                    {
                      text: 'OK',
                      onPress: () =>
                        setImagePreviewModal({ visible: false, imageUrl: '', imageName: '' }),
                    },
                  ]);
                }}
              />
            </View>

            {/* Botões de Ação */}
            <View
              style={{
                flexDirection: 'row',
                padding: 16,
                backgroundColor: 'white',
                borderTopWidth: 1,
                borderTopColor: '#e0e0e0',
              }}
            >
              <TouchableOpacity
                style={{
                  flex: 1,
                  backgroundColor: AVAQualisTheme.colors.primary,
                  padding: 12,
                  borderRadius: 4,
                  marginRight: 8,
                  alignItems: 'center',
                }}
                onPress={() => {
                  setImagePreviewModal({ visible: false, imageUrl: '', imageName: '' });
                  // Aqui você pode adicionar lógica para baixar a imagem se necessário
                  Alert.alert(
                    '📱 Imagem Local',
                    'Esta imagem está armazenada temporariamente no seu dispositivo. Para salvá-la permanentemente, faça o upload através da "Pasta de Upload".',
                    [{ text: 'OK' }]
                  );
                }}
              >
                <Text style={{ color: 'white', fontWeight: 'bold' }}>💾 Informações</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={{
                  flex: 1,
                  backgroundColor: '#f0f0f0',
                  padding: 12,
                  borderRadius: 4,
                  alignItems: 'center',
                }}
                onPress={() =>
                  setImagePreviewModal({ visible: false, imageUrl: '', imageName: '' })
                }
              >
                <Text style={{ color: '#666', fontWeight: 'bold' }}>Fechar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

export default AttachmentComponent;
